import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, Download, ChevronDown } from "lucide-react";
import useSEO from "../../lib/useSEO";
import { GUIDES, guideBySlug } from "../../data/seoGuides";
import { SeoShell, SeoHero, PlanCTA, Breadcrumb, crumbsLd } from "./SeoLayout";
import { useAiGuide, useAiGuides } from "../../lib/growthApi";

const RUNBOOK = "/downloads/guides/Capittance-S4HANA-Cutover-Runbook-214.xlsx";

function Section({ s }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-2xl font-extrabold text-[#0F1723]">{s.h}</h2>
      {s.p && <p className="mt-3 text-[15px] leading-relaxed text-zinc-700">{s.p}</p>}
      {s.list?.length > 0 && (
        <ul className="mt-3 space-y-2">
          {s.list.map((li) => <li key={li} className="flex gap-2 text-[15px] text-zinc-800"><span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#C9A66B" }}/>{li}</li>)}
        </ul>
      )}
    </section>
  );
}

function Faq({ faq }) {
  return (
    <section className="mt-12" data-testid="guide-faq">
      <h2 className="font-display text-2xl font-extrabold text-[#0F1723]">Frequently asked questions</h2>
      <div className="mt-4 divide-y border rounded-md bg-white" style={{ borderColor: "#F0D3D8" }}>
        {faq.map((f, i) => (
          <details key={f.q} className="group p-4" data-testid={`guide-faq-${i}`}>
            <summary className="cursor-pointer list-none flex items-center justify-between font-semibold text-[15px] text-[#0F1723]">
              {f.q}<ChevronDown size={16} className="transition-transform group-open:rotate-180"/>
            </summary>
            <p className="mt-2 text-sm text-zinc-700 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function Sidebar({ g }) {
  const related = (g.related.length ? g.related : ["erp-cutover-plan", "erp-go-live-checklist", "hypercare-plan"]).map(guideBySlug).filter(Boolean);
  return (
    <aside className="space-y-6 md:sticky md:top-6 self-start">
      <div className="p-5 rounded-md bg-white border" style={{ borderColor: "#F0D3D8" }}>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "#7A1F2B" }}>Free template</div>
        <div className="font-display text-lg font-extrabold mt-1">214-step cutover runbook</div>
        <p className="text-xs text-zinc-600 mt-1">Nine tracks, owners, timings and rollback paths. Excel.</p>
        <a href={RUNBOOK} download className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: "#7A1F2B" }} data-testid="guide-runbook-download"><Download size={14}/> Download</a>
      </div>
      <div className="p-5 rounded-md bg-white border" style={{ borderColor: "#F0D3D8" }}>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: "#7A1F2B" }}>Related guides</div>
        <ul className="mt-2 space-y-2">
          {related.map((r) => <li key={r.slug}><Link to={`/guides/${r.slug}`} className="text-sm text-zinc-800 hover:underline" data-testid={`guide-related-${r.slug}`}>{r.h1}</Link></li>)}
        </ul>
      </div>
      <Link to="/tools/go-live-readiness-calculator" className="block p-5 rounded-md text-white" style={{ background: "#0B2545" }} data-testid="guide-tool-link">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-sky-200">Free tool</div>
        <div className="font-display text-lg font-extrabold mt-1">Score your go-live readiness <ArrowRight size={14} className="inline"/></div>
      </Link>
    </aside>
  );
}

export default function GuidePage() {
  const { slug } = useParams();
  const staticGuide = guideBySlug(slug);
  const ai = useAiGuide(slug, !!staticGuide);
  const g = staticGuide || ai.guide;
  const crumbs = [["/", "Home"], ["/guides", "Guides"], [`/guides/${slug}`, g?.keyword || ""]];
  useSEO({
    title: g ? `${g.title} | Capittance` : "Guides | Capittance",
    description: g?.description,
    path: `/guides/${slug}`,
    type: "article",
    jsonLd: g && { "@context": "https://schema.org", "@graph": [
      { "@type": "Article", headline: g.title, description: g.description, author: { "@type": "Organization", name: "Capittance" }, publisher: { "@type": "Organization", name: "Capittance", logo: { "@type": "ImageObject", url: "https://capittance.com/og-cover.png" } }, mainEntityOfPage: `https://capittance.com/guides/${g.slug}` },
      { "@type": "FAQPage", mainEntity: g.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
      crumbsLd(crumbs),
    ] },
  });
  if (!g && ai.loading) return <SeoShell><div className="max-w-6xl mx-auto px-6 py-24 text-sm text-zinc-500" data-testid="guide-loading">Loading guide…</div></SeoShell>;
  if (!g) return <Navigate to="/guides" replace />;
  return (
    <SeoShell>
      <SeoHero eyebrow={`Guide · ${g.erp}`} h1={g.h1} intro={g.intro} />
      <main className="max-w-6xl mx-auto px-6 py-12" data-testid={`guide-${g.slug}`}>
        <Breadcrumb items={crumbs} />
        <div className="grid md:grid-cols-[1fr_300px] gap-10">
          <article>
            {g.sections.map((s) => <Section key={s.h} s={s} />)}
            <Faq faq={g.faq} />
          </article>
          <Sidebar g={g} />
        </div>
        <PlanCTA id="guide" />
      </main>
    </SeoShell>
  );
}

export function GuidesHub() {
  const aiGuides = useAiGuides();
  const all = [...aiGuides.map((g) => ({ ...g, isNew: true })), ...GUIDES];
  useSEO({
    title: "ERP Cutover, Testing & Go-Live Guides | Capittance",
    description: "Practical guides for SAP, Oracle and Workday programme leaders and PMOs: cutover plans, go-live checklists, testing, data migration, hypercare and RAID.",
    path: "/guides",
    jsonLd: { "@context": "https://schema.org", "@type": "CollectionPage", name: "Capittance guides", hasPart: all.map((g) => ({ "@type": "Article", headline: g.title, url: `https://capittance.com/guides/${g.slug}` })) },
  });
  return (
    <SeoShell>
      <SeoHero eyebrow="Guides · SAP · Oracle · Workday · PMO" h1="Guides for ERP go-lives and portfolio leaders." intro="Field-tested playbooks for cutover, testing, data migration, hypercare and executive portfolio reporting." />
      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="guides-grid">
          {all.map((g, i) => (
            <Link key={g.slug} to={`/guides/${g.slug}`} className="p-6 rounded-md bg-white border hover:-translate-y-0.5 transition-transform" style={{ borderColor: "#F0D3D8", animation: `fadeUp .4s ease ${i * 40}ms both` }} data-testid={`guides-card-${g.slug}`}>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2" style={{ color: "#7A1F2B" }}>{g.erp}{g.isNew && <span className="px-1.5 py-0.5 rounded-sm text-white" style={{ background: "#C9A66B" }}>New</span>}</div>
              <div className="font-display text-lg font-extrabold mt-1 text-[#0F1723]">{g.h1}</div>
              <p className="text-sm text-zinc-600 mt-2 line-clamp-3">{g.description}</p>
            </Link>
          ))}
        </div>
        <PlanCTA id="guides-hub" />
      </main>
    </SeoShell>
  );
}
